import { addDays, format, isAfter, isWithinInterval, parseISO, startOfDay } from 'date-fns';

export type SupportNoticeAudienceScope = 'all_organizations' | 'all_msps' | 'all_vendors' | 'selected_organizations';

export interface SupportNotice {
  id: string;
  message: string;
  startsAt: string;
  endsAt: string;
  appliesToAll: boolean;
  audienceScope: SupportNoticeAudienceScope;
  organizationTargets: Array<{ organizationId: string; organizationName?: string | null }>;
  createdAt?: string;
}

export interface SupportNoticeDraft {
  message: string;
  startsAt: string;
  endsAt: string;
  appliesToAll: boolean;
  audienceScope: SupportNoticeAudienceScope;
  organizationIds: string[];
}

export type SupportNoticeStatus = 'active' | 'scheduled' | 'ended';

function withMinutePrecision(value: Date) {
  const normalized = new Date(value);
  normalized.setSeconds(0, 0);
  return normalized;
}

export function toLocalDateTimeInputValue(value: Date | string) {
  const date = typeof value === 'string' ? parseISO(value) : value;
  return format(date, "yyyy-MM-dd'T'HH:mm");
}

export function createSupportNoticeDraftWindow(start: Date, end: Date): SupportNoticeDraft {
  return {
    message: '',
    startsAt: toLocalDateTimeInputValue(withMinutePrecision(start)),
    endsAt: toLocalDateTimeInputValue(withMinutePrecision(end)),
    appliesToAll: true,
    audienceScope: 'all_organizations',
    organizationIds: [],
  };
}

export function createDefaultSupportNoticeDraft(now: Date = new Date()) {
  const startsAt = withMinutePrecision(now);
  return createSupportNoticeDraftWindow(startsAt, addDays(startsAt, 1));
}

export function getNextWeekendSupportNoticeDraft(now: Date = new Date()) {
  const localNow = withMinutePrecision(now);
  const saturday = startOfDay(new Date(localNow));
  const daysUntilSaturday = (6 - saturday.getDay() + 7) % 7 || 7;
  saturday.setDate(saturday.getDate() + daysUntilSaturday);
  return createSupportNoticeDraftWindow(saturday, addDays(saturday, 2));
}

export function getSupportNoticeValidationError(draft: SupportNoticeDraft): string | null {
  if (!draft.message.trim()) {
    return 'Enter a notice message.';
  }

  if (!draft.startsAt) {
    return 'Choose a start date and time.';
  }

  if (!draft.endsAt) {
    return 'Choose an end date and time.';
  }

  const startsAt = new Date(draft.startsAt);
  const endsAt = new Date(draft.endsAt);

  if (Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime())) {
    return 'Choose valid start and end date/times.';
  }

  if (!isAfter(endsAt, startsAt)) {
    return 'End date and time must be after the start date and time.';
  }

  if (draft.audienceScope === 'selected_organizations' && draft.organizationIds.length === 0) {
    return 'Select at least one customer, MSP, or vendor.';
  }

  return null;
}

export function isSupportNoticeActive(
  notice: Pick<SupportNotice, 'startsAt' | 'endsAt'>,
  now: string | Date = new Date(),
) {
  const current = typeof now === 'string' ? parseISO(now) : now;
  return isWithinInterval(current, {
    start: parseISO(notice.startsAt),
    end: parseISO(notice.endsAt),
  });
}

export function getSupportNoticeStatus(
  notice: Pick<SupportNotice, 'startsAt' | 'endsAt'>,
  now: string | Date = new Date(),
): SupportNoticeStatus {
  const current = typeof now === 'string' ? parseISO(now) : now;
  const startsAt = parseISO(notice.startsAt);
  const endsAt = parseISO(notice.endsAt);

  if (isWithinInterval(current, { start: startsAt, end: endsAt })) {
    return 'active';
  }

  return current < startsAt ? 'scheduled' : 'ended';
}

export function formatSupportNoticeWindow(notice: Pick<SupportNotice, 'startsAt' | 'endsAt'>) {
  return `${format(parseISO(notice.startsAt), 'MMM d, yyyy, h:mm a')} to ${format(parseISO(notice.endsAt), 'MMM d, yyyy, h:mm a')}`;
}

export function formatSupportNoticeDateTime(value: string) {
  return format(parseISO(value), 'MMM d, yyyy, h:mm a');
}

export function getSupportNoticeAudienceLabel(
  audienceScope: SupportNoticeAudienceScope,
  organizationTargets: Array<{ organizationId: string; organizationName?: string | null }>,
  vendorOrganizationIds: Iterable<string> = [],
) {
  if (audienceScope === 'all_organizations') {
    return 'All organisations';
  }

  if (audienceScope === 'all_msps') {
    return 'All MSPs';
  }

  if (audienceScope === 'all_vendors') {
    return 'All vendors';
  }

  const vendorIdSet = new Set(vendorOrganizationIds);
  if (
    vendorIdSet.size > 0 &&
    organizationTargets.length > 0 &&
    organizationTargets.every((target) => vendorIdSet.has(target.organizationId)) &&
    organizationTargets.length === vendorIdSet.size
  ) {
    return 'All vendors';
  }

  return organizationTargets.map((target) => target.organizationName ?? target.organizationId).join(', ');
}

export function toSupportNoticeInsert(draft: SupportNoticeDraft) {
  return {
    applies_to_all: draft.audienceScope === 'all_organizations',
    audience_scope: draft.audienceScope,
    message: draft.message.trim(),
    starts_at: new Date(draft.startsAt).toISOString(),
    ends_at: new Date(draft.endsAt).toISOString(),
  };
}

export function toSupportNoticeSaveParams(
  draft: SupportNoticeDraft,
  noticeId: string | null = null,
  _vendorOrganizationIds: string[] = [],
) {
  const organizationIds = draft.audienceScope === 'selected_organizations' ? draft.organizationIds : [];

  return {
    p_notice_id: noticeId,
    p_message: draft.message.trim(),
    p_starts_at: new Date(draft.startsAt).toISOString(),
    p_ends_at: new Date(draft.endsAt).toISOString(),
    p_audience_scope: draft.audienceScope,
    p_organization_ids: organizationIds,
  };
}

export function fromSupportNoticeRow(row: {
  id: string;
  message: string;
  starts_at: string;
  ends_at: string;
  applies_to_all?: boolean;
  audience_scope?: SupportNoticeAudienceScope | null;
  support_notice_targets?: Array<{
    organization_id: string;
    organizations?: { id: string; name: string } | null;
  }>;
  created_at?: string;
}) {
  return {
    id: row.id,
    message: row.message,
    startsAt: row.starts_at,
    endsAt: row.ends_at,
    appliesToAll: row.applies_to_all ?? true,
    audienceScope: row.audience_scope ?? ((row.applies_to_all ?? true) ? 'all_organizations' : 'selected_organizations'),
    organizationTargets: (row.support_notice_targets ?? []).map((target) => ({
      organizationId: target.organization_id,
      organizationName: target.organizations?.name ?? null,
    })),
    createdAt: row.created_at,
  } satisfies SupportNotice;
}
